import React, { Component, Fragment } from 'react';
import { NavLink as RRNavLink, Link, useNavigate } from 'react-router-dom';
import { connect } from 'react-redux';
import PropTypes from 'prop-types';
import { 
  GraduationCap, User, UserRound, Video, ChevronDown, Calendar, 
  Award, Gift, MessageCircle, Bell, Home, Settings,
  LayoutGrid, BookOpen, Menu, UserX, FileText, HelpCircle
} from 'lucide-react';
import RegisterModal from './auth/RegisterModal';
import LoginModal from './auth/LoginModal';
// Note: Login/Register modals kept for any remaining usages; navbar now uses page links
import Logout from './auth/Logout';
import { endImpersonation } from '../actions/authActions';
import { getUnreadCount } from '../actions/messageActions';
import { getStudentSubmissionForms } from '../actions/submissionFormActions';
import { getNotifications, markRead, markAllRead, addNotification } from '../actions/notificationActions';
import * as socketService from '../services/socketService';
import { subscribeToPush } from '../services/pushService';
import NotificationDropdown from './NotificationDropdown';

class AppNavbar extends Component {
  state = {
    isOpen: false,
    dropdownOpen: false,
    notificationOpen: false,
    isAnimating: false
  };

  static propTypes = {
    auth: PropTypes.object.isRequired,
    message: PropTypes.object.isRequired,
    navigate: PropTypes.func.isRequired,
    getUnreadCount: PropTypes.func.isRequired
  };

  componentDidMount() {
    if (this.props.auth.isAuthenticated) {
      this.props.getUnreadCount();
      this.props.getNotifications();
      this._connectSocket();
      if (this.props.auth.user?.role === 'student') {
        this.props.getStudentSubmissionForms();
      }
    }
  }

  componentDidUpdate(prevProps) {
    // User just logged in
    if (!prevProps.auth.isAuthenticated && this.props.auth.isAuthenticated) {
      this.props.getUnreadCount();
      this.props.getNotifications();
      this._connectSocket();
      if (this.props.auth.user?.role === 'student') {
        this.props.getStudentSubmissionForms();
      }
    }

    // User just logged out
    if (prevProps.auth.isAuthenticated && !this.props.auth.isAuthenticated) {
      this._disconnectSocket();
    }
  }

  componentWillUnmount() {
    this._disconnectSocket();
  }

  _connectSocket() {
    const token = this.props.auth.token;
    if (!token) return;
    socketService.connect(token);
    subscribeToPush(token);
    this._onNewMessage = () => {
      this.props.getUnreadCount();
    };
    socketService.on('new_message', this._onNewMessage);

    this._onNewNotification = (notification) => {
      this.props.addNotification(notification);
    };
    socketService.on('new_notification', this._onNewNotification);
  }

  _disconnectSocket() {
    if (this._onNewMessage) {
      socketService.off('new_message', this._onNewMessage);
      this._onNewMessage = null;
    }
    if (this._onNewNotification) {
      socketService.off('new_notification', this._onNewNotification);
      this._onNewNotification = null;
    }
    socketService.disconnect();
  }

  toggle = () => {
    this.setState(prevState => ({
      isOpen: !prevState.isOpen,
      isAnimating: true
    }));
    // Reset animation flag after animation completes
    setTimeout(() => {
      this.setState({ isAnimating: false });
    }, 300);
  };

  toggleDropdown = () => {
    this.setState({
      dropdownOpen: !this.state.dropdownOpen,
      notificationOpen: false
    });
  };

  handleNavigation = (path, isExternal = false) => {
    // Start closing animation
    this.setState({ isOpen: false, dropdownOpen: false, isAnimating: true });

    // If navigating to messages, refresh unread count after a short delay
    if (path === '/messages') {
      setTimeout(() => {
        this.props.getUnreadCount();
      }, 1000);
    }

    // Wait for animation to complete before navigating
    setTimeout(() => {
      this.setState({ isAnimating: false });
      if (isExternal) {
        window.location.href = path;
      } else {
        this.props.navigate(path);
      }
    }, 300);
  };

  // Check if user has access to repeatable submission forms
  checkUserHasRepeatableSubmissionForms = (user) => {
    if (!user || user.role !== 'student') return false;
    const forms = this.props.submissionForm?.submissionForms;
    return Array.isArray(forms) && forms.length > 0;
  };

  getMobileNavItems = (isAuthenticated, userRole, hasRepeatableSubmissionForms = false) => {
    if (!isAuthenticated) {
      return [
        { icon: Home, label: 'Home', path: '/' },
        { icon: Menu, label: 'Menu', isMenu: true }
      ];
    }

    // Base items for all authenticated users
    const baseItems = [
      { icon: Home, label: 'Home', path: '/' }
    ];

    // Role-specific items
    if (userRole === 'admin' || userRole === 'platform_admin') {
      return [
        ...baseItems,
        { icon: LayoutGrid, label: 'Content Management', path: '/content-management/lessons' },
        { icon: Settings, label: 'Settings', path: '/settings' },
        { icon: MessageCircle, label: 'Messages', path: '/messages', showBadge: true },
        { icon: User, label: 'Profile', path: '/profile' },
        { icon: Menu, label: 'Menu', isMenu: true }
      ];
    } else if (userRole === 'student') {
      // For students, show: Home, Messages, Submit (if applicable), Profile, Menu
      const studentItems = [
        ...baseItems,
        { icon: MessageCircle, label: 'Messages', path: '/messages', showBadge: true }
      ];

      // Add Submit button between Messages and Profile if user has repeatable submission forms
      if (hasRepeatableSubmissionForms) {
        studentItems.push({
          icon: null,
          label: 'Submit',
          path: '/submissions',
          isCircular: true
        });
      }

      studentItems.push(
        { icon: User, label: 'Profile', path: '/profile' },
        { icon: Menu, label: 'Menu', isMenu: true }
      );

      return studentItems;
    } else {
      // For instructors and other roles, show: Home, Messages, Profile, Menu
      return [
        ...baseItems,
        { icon: LayoutGrid, label: 'Content Management', path: '/content-management/lessons' },
        { icon: MessageCircle, label: 'Messages', path: '/messages', showBadge: true },
        { icon: User, label: 'Profile', path: '/profile' },
        { icon: Menu, label: 'Menu', isMenu: true }
      ];
    }
  };

  render() {
    const { isAuthenticated, user } = this.props.auth;
    const { unreadCount } = this.props.message;
    const { notifications, unreadCount: unreadNotifications } = this.props.notification;
    const userFullName = user ? `${user.name} ${user.lastName || ''}` : '';
    const userInitials = user ? `${user.name?.[0] || ''}${user.lastName?.[0] || ''}`.toUpperCase() : '';

    // Check if user has repeatable submission forms (needed for both mobile and desktop navigation)
    const hasRepeatableSubmissionForms = this.checkUserHasRepeatableSubmissionForms(user);

    const authLinks = (
      <Fragment>
        <li className="flex items-center relative mr-4 space-x-2">
          {/* <button
            onClick={() => window.location.href = '/events'}
            className="flex items-center text-gray-300 hover:text-white focus:outline-none"
            title="Events"
          >
            <Calendar className="h-6 w-6" />
          </button> */}
          <button
            onClick={() => this.handleNavigation('/messages')}
            className="relative flex items-center text-gray-300 hover:text-white focus:outline-none"
            title="Messages"
          >
            <MessageCircle className="h-6 w-6" />
            {unreadCount > 0 && (
              <span className="absolute -top-1 -right-1 bg-blue-400 text-white text-[10px] font-bold rounded-full px-1 py-0 shadow border-2 border-gray-800 min-w-[16px] text-center">
                {unreadCount > 99 ? '99+' : unreadCount}
              </span>
            )}
          </button>
          {user?.role !== 'admin' && user?.role !== 'platform_admin' && (
            <div className="relative">
              <button
                onClick={() => this.setState(s => ({ notificationOpen: !s.notificationOpen, dropdownOpen: false }))}
                className="relative flex items-center text-gray-300 hover:text-white focus:outline-none"
                title="Notifications"
              >
                <Bell className="h-6 w-6" />
                {unreadNotifications > 0 && (
                  <span className="absolute -top-1 -right-1 bg-red-500 text-white text-[10px] font-bold rounded-full px-1 py-0 shadow border-2 border-gray-800 min-w-[16px] text-center">
                    {unreadNotifications > 99 ? '99+' : unreadNotifications}
                  </span>
                )}
              </button>
              {this.state.notificationOpen && (
                <NotificationDropdown
                  notifications={notifications}
                  onMarkRead={(id) => this.props.markRead(id)}
                  onMarkAllRead={() => this.props.markAllRead()}
                  onClose={() => this.setState({ notificationOpen: false })}
                />
              )}
            </div>
          )}
        </li>
        <li className="flex items-center relative">
          {/* Welcome Message and Profile */}
          <div className="flex items-center space-x-3">
            <span className="text-[#1086a4] font-medium hidden sm:block">
              {userFullName && (
                <>
                  <span className="text-gray-300">Hi,</span>
                  <span className="ml-1 text-white hover:text-[#1086a4] transition-colors duration-200">{userFullName}</span>
                </>
              )}
            </span>
            {/* Profile Icon with Status Dot and Dropdown Arrow */}
            <div
              className="relative flex items-center cursor-pointer group"
              onClick={this.toggleDropdown}
              tabIndex={0}
              aria-label="Open profile menu"
              style={{ outline: 'none' }}
            >
              <div className="w-10 h-10 rounded-full bg-[#1086a4] bg-opacity-10 flex items-center justify-center overflow-hidden border-2 border-[#1086a4] transition-all duration-200 group-hover:border-white">
                {user?.profilePicture?.url || user?.profilePicture?.data ? (
                  <img 
                    src={user.profilePicture.url || `data:${user.profilePicture.contentType};base64,${user.profilePicture.data}`}
                    alt="Profile"
                    className="w-full h-full object-cover"
                  />
                ) : (
                  <div className="w-full h-full bg-[#1086a4] bg-opacity-10 flex items-center justify-center">
                    <UserRound className="w-6 h-6 text-[#1086a4]" />
                  </div>
                )}
              </div>
              {/* Online Status Dot */}
              <span className="absolute top-0 right-6 block w-3 h-3 bg-green-400 border-2 border-gray-800 rounded-full"></span>
              {/* Dropdown Arrow */}
              <ChevronDown
                className={`w-5 h-5 ml-2 text-[#1086a4] transition-transform duration-200 ${this.state.dropdownOpen ? 'transform rotate-180' : ''}`}
              />
            </div>
          </div>
          {/* Dropdown Menu */}
          {this.state.dropdownOpen && (
            <div className="absolute right-0 top-full mt-2 w-64 rounded-xl shadow-lg bg-white border border-[#f9fafb] z-50 overflow-hidden">
              {/* User Info Header */}
              <div className="px-4 py-3 bg-[#f9fafb] border-b border-gray-100">
                <p className="text-sm text-[#1e2d40] font-medium">{userFullName}</p>
                <p className="text-xs text-gray-500 mt-1">{user?.email}</p>
              </div>
              {/* Menu Items */}
              <div className="py-2">
                <Link
                  to="/profile"
                  className="flex items-center px-4 py-3 text-sm text-[#1e2d40] hover:bg-[#f9fafb] hover:text-[#1086a4] transition-colors duration-200"
                  onClick={() => this.setState({ dropdownOpen: false })}
                >
                  <User className="w-4 h-4 mr-3" />
                  View Profile
                </Link>
                <div className="border-t border-gray-100">
                  <Logout />
                </div>
              </div>
            </div>
          )}
        </li>
      </Fragment>
    );

    const guestLinks = (
      <Fragment>
        <li>
          <Link to="/register" className="text-gray-300 hover:text-white px-3 py-2 rounded-md text-sm font-medium transition-colors duration-200">
            Register
          </Link>
        </li>
        <li>
          <Link to="/login" className="text-gray-300 hover:text-white px-3 py-2 rounded-md text-sm font-medium transition-colors duration-200">
            Login
          </Link>
        </li>
      </Fragment>
    );

    const adminLinks = (
      <Fragment>
        <li>
          <button onClick={() => this.handleNavigation('/')}
            className="text-gray-300 hover:text-white px-3 py-2 rounded-md text-sm font-medium w-full text-left">
            Home
          </button>
        </li>
        <li>
          <button onClick={() => this.handleNavigation('/content-management/lessons')}
            className="text-gray-300 hover:text-white px-3 py-2 rounded-md text-sm font-medium w-full text-left">
            Content Management
          </button>
        </li>
        <li>
          <button onClick={() => this.handleNavigation('/settings')}
            className="text-gray-300 hover:text-white px-3 py-2 rounded-md text-sm font-medium w-full text-left">
            Settings
          </button>
        </li>
        <li>
          <button onClick={() => this.handleNavigation('/contact-help')}
            className="text-gray-300 hover:text-white px-3 py-2 rounded-md text-sm font-medium w-full text-left">
            Contact & Help
          </button>
        </li>
      </Fragment>
    );

    const studentLinks = (
      <Fragment>
        {/* Home and Submit are already in the mobile bottom bar, so only show desktop-hidden items here */}
        <li className="hidden md:block">
          <button onClick={() => this.handleNavigation('/')}
            className="text-gray-300 hover:text-white px-3 py-2 rounded-md text-sm font-medium w-full text-left">
            Home
          </button>
        </li>
        {hasRepeatableSubmissionForms && (
          <li className="hidden md:block">
            <button onClick={() => this.handleNavigation('/submissions')}
              className="relative bg-gradient-to-r from-[#1086a4] to-[#054581] text-white px-4 py-1.5 rounded-full text-sm font-semibold shadow-md shadow-[#1086a4]/30 hover:shadow-lg hover:shadow-[#1086a4]/40 transition-all animate-pulse">
              Submit
            </button>
          </li>
        )}
        <li>
          <button onClick={() => this.handleNavigation('/leaderboard')}
            className="text-gray-300 hover:text-white px-3 py-2 rounded-md text-sm font-medium w-full text-left">
            Leaderboard
          </button>
        </li>
        <li>
          <button onClick={() => this.handleNavigation('/contact-help')}
            className="text-gray-300 hover:text-white px-3 py-2 rounded-md text-sm font-medium w-full text-left">
            Contact & Help
          </button>
        </li>
      </Fragment>
    );

    const instructorLinks = (
      <Fragment>
        <li>
          <button onClick={() => this.handleNavigation('/')}
            className="text-gray-300 hover:text-white px-3 py-2 rounded-md text-sm font-medium w-full text-left">
            Home
          </button>
        </li>
        <li>
          <button onClick={() => this.handleNavigation('/content-management/lessons')}
            className="text-gray-300 hover:text-white px-3 py-2 rounded-md text-sm font-medium w-full text-left">
            Content Management
          </button>
        </li>
        <li>
          <button onClick={() => this.handleNavigation('/contact-help')}
            className="text-gray-300 hover:text-white px-3 py-2 rounded-md text-sm font-medium w-full text-left">
            Contact & Help
          </button>
        </li>
      </Fragment>
    );

    // Get role-based navigation items
    const mobileNavItems = this.getMobileNavItems(isAuthenticated, user?.role, hasRepeatableSubmissionForms);

    return (
      <>
        {/* Impersonation Notification Bar */}
        {this.props.auth.isImpersonating && (
          <div className="bg-orange-500 text-white px-4 py-2 text-center">
            <div className="flex items-center justify-center space-x-4">
              <span className="text-sm font-medium">
                🔄 You are impersonating: <strong>{user?.name} {user?.lastName}</strong> 
                (Original: {this.props.auth.originalUser?.name} {this.props.auth.originalUser?.lastName})
              </span>
              <button
                onClick={() => this.props.endImpersonation()}
                className="bg-white text-orange-500 px-3 py-1 rounded text-sm font-medium hover:bg-gray-100 transition-colors flex items-center space-x-1"
              >
                <UserX className="h-4 w-4" />
                <span>Return to Admin</span>
              </button>
            </div>
          </div>
        )}

        {/* Top Navigation - Hidden on mobile for students */}
        <nav className={`bg-gray-800 ${isAuthenticated && user?.role === 'student' ? 'hidden md:block' : ''}`}>
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="flex items-center justify-between h-16">
              <div className="flex items-center">
                <a href="/" className="flex items-center gap-2 text-white font-bold text-xl mr-8">
                  <GraduationCap className="h-8 w-8" />
                  Your Big Year
                </a>
                <div className="hidden md:block">
                  <ul className="flex space-x-4">
                    {isAuthenticated && (user.role === 'admin' || user.role === 'platform_admin') ? adminLinks : null}
                    {isAuthenticated && user.role === 'student' ? studentLinks : null}
                    {isAuthenticated && user.role === 'instructor' ? instructorLinks : null}
                  </ul>
                </div>
              </div>

              <div className="hidden md:block">
                <ul className="flex items-center space-x-4">
                  {isAuthenticated ? authLinks : guestLinks}
                </ul>
              </div>
            </div>
          </div>
        </nav>

        {/* Mobile menu overlay */}
        {this.state.isOpen && (
          <div
            className="lg:hidden fixed inset-0 bg-black bg-opacity-50 z-40 transition-opacity duration-300"
            onClick={this.toggle}
          />
        )}

        {/* Enhanced Mobile menu - Updated to slide from bottom */}
        <div className={`
          lg:hidden fixed left-0 right-0 bottom-16 bg-gray-800 z-50
          transform transition-transform duration-300 ease-in-out
          ${this.state.isOpen ? 'translate-y-0' : 'translate-y-full'}
          ${this.state.isAnimating ? 'shadow-xl' : ''}
          flex flex-col max-h-[80vh] rounded-t-xl
        `}>
          {/* Main Navigation Section */}
          <div className="flex-1 overflow-y-auto">
            <div className="p-4 border-b border-gray-700">
              <ul className="space-y-2">
                {isAuthenticated && (user.role === 'admin' || user.role === 'platform_admin') && adminLinks}
                {isAuthenticated && user.role === 'student' && studentLinks}
                {isAuthenticated && user.role === 'instructor' && instructorLinks}
                {!isAuthenticated && guestLinks}
              </ul>
            </div>

            {/* Quick Actions Section */}
            {/* {isAuthenticated && (
              <div className="p-4 border-b border-gray-700">
                <h3 className="text-sm font-medium text-gray-400 mb-3">Quick Actions</h3>
                <div className="grid grid-cols-3 gap-4">
                  <button onClick={() => this.handleNavigation('/events', true)}
                    className="flex flex-col items-center justify-center p-2 rounded-lg hover:bg-gray-700 transition-colors">
                    <Calendar className="h-6 w-6 text-gray-300" />
                    <span className="text-xs text-gray-400 mt-1">Events</span>
                  </button>
                  <button onClick={() => this.handleNavigation('/notifications', true)}
                    className="flex flex-col items-center justify-center p-2 rounded-lg hover:bg-gray-700 transition-colors">
                    <Bell className="h-6 w-6 text-gray-300" />
                    <span className="text-xs text-gray-400 mt-1">Alerts</span>
                  </button>
                </div>
              </div>
            )} */}
          </div>

          {/* Profile Section */}
          {isAuthenticated && (
            <div className="border-t border-gray-700 p-4">
              <button
                className="flex items-center w-full p-3 rounded-lg hover:bg-gray-700 transition-colors"
                onClick={this.toggleDropdown}
              >
                <div className="w-10 h-10 rounded-full bg-[#1086a4] bg-opacity-10 flex items-center justify-center border-2 border-[#1086a4] overflow-hidden">
                  {user?.profilePicture?.url || user?.profilePicture?.data ? (
                    <img
                      src={user.profilePicture.url || `data:${user.profilePicture.contentType};base64,${user.profilePicture.data}`}
                      alt="Profile"
                      className="w-full h-full object-cover"
                    />
                  ) : (
                    <div className="w-full h-full bg-[#1086a4] bg-opacity-10 flex items-center justify-center">
                      <UserRound className="w-6 h-6 text-[#1086a4]" />
                    </div>
                  )}
                </div>
                <div className="ml-3 flex-1 text-left">
                  <p className="text-sm font-medium text-white">{userFullName}</p>
                  <p className="text-xs text-gray-400">{user?.email}</p>
                </div>
                <ChevronDown
                  className={`w-5 h-5 text-gray-400 transition-transform duration-200 ${
                    this.state.dropdownOpen ? 'transform rotate-180' : ''
                  }`}
                />
              </button>

              {/* Profile Dropdown - Updated Styling */}
              {this.state.dropdownOpen && (
                <div className="mt-2 py-2 px-1">
                  <button
                    onClick={() => this.handleNavigation('/profile')}
                    className="flex items-center w-full px-4 py-3 text-sm text-gray-300 hover:bg-gray-700 hover:text-white transition-colors rounded-lg"
                  >
                    <User className="w-5 h-5 mr-3 text-gray-400" />
                    <span>View Profile</span>
                  </button>

                  {/* Separator */}
                  <div className="my-2 border-t border-gray-700" />

                  {/* Wrap Logout component to ensure consistent styling */}
                  <div className="px-1">
                    <Logout
                      className="flex items-center w-full px-4 py-3 text-sm text-gray-300 hover:bg-gray-700 hover:text-white transition-colors rounded-lg"
                    />
                  </div>
                </div>
              )}
            </div>
          )}

          {/* Pull indicator */}
          <div className="absolute -top-3 left-1/2 transform -translate-x-1/2">
            <div className="w-12 h-1 bg-gray-600 rounded-full mx-auto" />
          </div>
        </div>

        {/* Mobile Bottom Navigation - Moved outside top nav to stay visible */}
        <nav className="lg:hidden fixed bottom-0 left-0 right-0 bg-gray-800 border-t border-gray-700 z-50">
          <div className="flex items-center justify-around h-16">
            {mobileNavItems.map((item, index) => {
              const Icon = item.icon;
              const isActive = !item.isMenu && window.location.pathname === item.path;

              // Elevated "Submit" button with pulse effect
              if (item.isCircular) {
                return (
                  <button
                    key={index}
                    onClick={() => this.handleNavigation(item.path)}
                    className="flex flex-col items-center justify-center w-full h-full relative group -mt-5"
                  >
                    <div className="relative">
                      <span className="absolute inset-0 rounded-full bg-[#1086a4] animate-ping opacity-25" />
                      <div className="relative w-12 h-12 rounded-full bg-gradient-to-br from-[#1086a4] to-[#054581] flex items-center justify-center shadow-lg shadow-[#1086a4]/40">
                        <span className="text-white text-xs font-bold tracking-wide">GO</span>
                      </div>
                    </div>
                    <span className="text-[10px] mt-1 font-semibold text-[#1086a4]">{item.label}</span>
                  </button>
                );
              }

              return (
                <button
                  key={index}
                  onClick={() => item.isMenu ? this.toggle() : this.handleNavigation(item.path)}
                  className="flex flex-col items-center justify-center w-full h-full relative group"
                >
                  <div className={`flex flex-col items-center transition-all duration-200 ${
                    isActive ? 'text-white' : 'text-gray-400 hover:text-white'
                  }`}>
                    <div className="relative">
                      {Icon && <Icon className={`w-6 h-6 ${item.isMenu && this.state.isOpen ? 'transform rotate-180' : ''}`} />}
                      {/* Show unread count badge for Messages */}
                      {item.showBadge && unreadCount > 0 && (
                        <span className="absolute -top-1 -right-1 bg-blue-400 text-white text-[10px] font-bold rounded-full px-1 py-0 shadow border-2 border-gray-800 min-w-[16px] text-center">
                          {unreadCount > 99 ? '99+' : unreadCount}
                        </span>
                      )}
                    </div>
                    <span className="text-xs mt-1 font-medium">{item.label}</span>
                  </div>

                  {/* Active Indicator */}
                  {!item.isMenu && (
                    <span className={`absolute bottom-0 left-1/2 transform -translate-x-1/2 w-12 h-0.5 rounded-full transition-all duration-200 ${
                      isActive ? 'bg-[#1086a4]' : 'bg-transparent'
                    }`} />
                  )}
                </button>
              );
            })}
          </div>
        </nav>
      </>
    );
  }
}

const mapStateToProps = state => ({
  auth: state.auth,
  message: state.message,
  submissionForm: state.submissionForm,
  notification: state.notification
});

// Create a wrapper component to use the hook
const AppNavbarWithNavigate = props => {
  const navigate = useNavigate();
  return <AppNavbar {...props} navigate={navigate} />;
};

export default connect(
  mapStateToProps,
  { endImpersonation, getUnreadCount, getStudentSubmissionForms, getNotifications, markRead, markAllRead, addNotification }
)(AppNavbarWithNavigate);
